"use client";

import { VehicleMedia } from "@alba-cars/common-modules";
import { useRef, useState } from "react";
import type { Swiper as SwiperClass } from "swiper";
import { FreeMode, Navigation, Thumbs, Virtual } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

import OptimizedImage from "@/components/common/images/OptimizedImage";
import IconArrowRight from "@/components/Icons/IconArrowRight";
import { formatImageUrl } from "@/lib/utils";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

interface DesktopGalleryProps {
  images: VehicleMedia[];
  altText: string;
  onImageClickAction: (index: number) => void;
}

export default function DesktopGallery({ images, altText, onImageClickAction }: DesktopGalleryProps) {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperClass | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef<SwiperClass | null>(null);

  const handleSlideClick = (index: number): void => {
    onImageClickAction(index);
  };

  const handleThumbnailClick = (index: number): void => {
    setActiveIndex(index);
    if (swiperRef.current) {
      swiperRef.current.slideToLoop(index);
    }
  };

  return (
    <div className="group relative z-0 hidden lg:block">
      <div className="relative mb-4 h-full w-full overflow-hidden rounded-3xl lg:h-[600px]">
        <Swiper
          onSwiper={(swiper) => (swiperRef.current = swiper)}
          spaceBetween={0}
          direction="horizontal"
          navigation={{
            nextEl: ".custom-swiper-button-next",
            prevEl: ".custom-swiper-button-prev",
          }}
          loop={true}
          thumbs={{ swiper: thumbsSwiper }}
          modules={[Navigation, Thumbs, Virtual]}
          preventInteractionOnTransition={true}
          virtual={{
            addSlidesAfter: 1,
            addSlidesBefore: 1,
            enabled: true,
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          className="h-full w-full"
        >
          {images.map((image, index) => (
            <SwiperSlide key={image.id || index} className="h-full w-full" virtualIndex={index}>
              <div
                className="relative h-full w-full cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => handleSlideClick(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleSlideClick(index);
                  }
                }}
              >
                <OptimizedImage
                  src={formatImageUrl(image.mediaUrl) ?? DEFAULT_PLACEHOLDER}
                  alt={`${altText}-${index + 1}`}
                  fill
                  priority={index < 3}
                  fetchPriority={index < 3 ? "high" : "auto"}
                  className="h-full w-full object-contain object-bottom"
                  imageLayout="custom"
                  sizes="(max-width: 1280px) 100vw, 1280px"
                  placeholder={"blur"}
                  blurDataURL={DEFAULT_PLACEHOLDER}
                  loading={index < 3 ? "eager" : "lazy"}
                />
              </div>
            </SwiperSlide>
          ))}

          <div className="custom-swiper-button-prev fade-in fade-out absolute left-0 top-0 z-20 flex h-full w-10 cursor-pointer items-center justify-center rounded-bl-2xl rounded-tl-2xl bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <button aria-label="Show previous image">
              <IconArrowRight fill="white" width="25" height="25" className="-rotate-180" />
            </button>
          </div>

          <div className="custom-swiper-button-next fade-out absolute right-0 top-0 z-20 flex h-full w-10 cursor-pointer items-center justify-center rounded-br-2xl rounded-tr-2xl bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
            <button aria-label="Show next image">
              <IconArrowRight fill="white" width="25" height="25" />
            </button>
          </div>
        </Swiper>
      </div>

      <div className="relative h-32 w-full overflow-hidden rounded-xl">
        <Swiper
          spaceBetween={8}
          breakpoints={{
            1024: { slidesPerView: 3 },
            1300: {
              slidesPerView: 3,
              spaceBetween: 20,
            },
            1880: {
              slidesPerView: 5,
            },
          }}
          freeMode={true}
          watchSlidesProgress={true}
          direction="horizontal"
          modules={[FreeMode, Navigation, Thumbs]}
          className="h-full"
          preventInteractionOnTransition={true}
          loop={true}
          onSwiper={setThumbsSwiper}
        >
          {images.map((image, index) => (
            <SwiperSlide key={`thumb-${image.id}-${index}`} className="h-full">
              <div
                className={`relative h-full w-full cursor-pointer overflow-hidden rounded-2xl border-2 ${
                  activeIndex === index ? "border-primary" : "border-transparent"
                }`}
                role="button"
                tabIndex={0}
                onClick={() => handleThumbnailClick(index)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    handleThumbnailClick(index);
                  }
                }}
              >
                <OptimizedImage
                  src={formatImageUrl(image.mediaUrl) ?? DEFAULT_PLACEHOLDER}
                  alt={`${altText}-thumbnail-${index + 1}`}
                  width={200}
                  height={200}
                  className="h-full w-full object-cover object-bottom"
                  imageLayout="custom"
                  sizes="200px"
                  loading="lazy"
                  blurDataURL={image?.placeholder ?? DEFAULT_PLACEHOLDER}
                />
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}
